import axios from 'axios';
import { getToken, removeToken } from './tokenStorage';

// Development mode flag - set to true to bypass OTP
export const DEV_MODE = true;

// API Base path - now using a relative URL which will work with the proxy in package.json
const BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

// API Endpoints
const ENDPOINTS = {
  LOGIN: '/login/',
  USER_INFO: '/user-info/',
  CUSTOMERS: '/collector/customers/',
  COLLECTIONS: '/collector/collections/',
  MARKET_PRICES: '/collector/market-milk-prices/',
  DAIRY_INFO: '/collector/dairy-information/',
  WALLET: '/wallet/',
  RAW_COLLECTIONS: '/collector/raw-collections/',
  // Pro-Rata Report Endpoints
  PRO_RATA_PURCHASE_REPORT: '/collector/pro-rata-reports/purchase-report/',
  PRO_RATA_PURCHASE_SUMMARY_REPORT: '/collector/pro-rata-reports/purchase-summary-report/',
  PRO_RATA_FULL_REPORT: '/collector/pro-rata-reports/full-report/',
  PRO_RATA_CUSTOMER_BILLS: '/collector/pro-rata-reports/customer-bills/',
  PRO_RATA_CUSTOMER_REPORT: '/collector/pro-rata-reports/customer-report/',
  PRO_RATA_PURCHASE_SUMMARY_DATA: '/collector/pro-rata-reports/purchase-summary-data/',
};

// Create axios instance with optimized settings
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout
});

// Add authorization token to requests
api.interceptors.request.use(
  async (config) => {
    const token = await getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle common response errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle CORS errors specifically
    if (error.message === 'Network Error' || !error.response) {
      console.error('Network Error Details:', error);
      // Create a more helpful error message for debugging
      let errorMessage = 'Network connection error. Please check your internet connection.';

      // If we're in development, provide more detailed information
      if (DEV_MODE) {
        errorMessage += ' This might be a CORS issue or certificate problem.';
      }

      return Promise.reject({
        error: errorMessage
      });
    }

    switch (error.response.status) {
      case 401: {
        // Handle unauthorized - clear token and return to login
        // But do NOT force-redirect if user is on public landing routes
        const pathname = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : '';
        const onPublicLanding = pathname === '/' || pathname.startsWith('/landing_page');
        if (!onPublicLanding) {
          removeToken();
          window.location.href = '/login';
        }
        return Promise.reject({
          error: 'Your session has expired. Please login again.'
        });
      }
      case 404:
        return Promise.reject({
          error: 'The requested resource was not found.'
        });
      case 500:
        return Promise.reject({
          error: 'Server error. Please try again later.'
        });
      default:
        return Promise.reject(error.response?.data || {
          error: `Network error occurred (${error.response?.status || 'unknown'})`
        });
    }
  }
);

// Authentication APIs
export const loginUser = async (phoneNumber) => {
  try {
    const response = await api.post(ENDPOINTS.LOGIN, {
      phone_number: phoneNumber.replace('+91', '') // Remove +91 prefix if present
    });
    return response.data;
  } catch (error) {
    console.error('Login API Error:', error);
    throw error;
  }
};

// OTP verification API (second step of login)
// Tries the canonical verify endpoint. Backend should respond with a token on success.
export const verifyOtp = async (phoneNumber, otpCode, options = {}) => {
  try {
    const payload = {
      phone_number: phoneNumber.replace('+91', ''),
      otp: String(otpCode).trim(),
    };
    // Attach optional session identifiers if backend provides them
    if (options.session_id) payload.session_id = options.session_id;
    if (options.transaction_id) payload.transaction_id = options.transaction_id;
    // Some backends require a verificationId from step 1
    if (options.verificationId) payload.verificationId = options.verificationId;
    // For compatibility with backends expecting 'code' instead of 'otp'
    payload.code = payload.otp;

    const response = await api.post('/verify-otp/', payload);
    return response.data;
  } catch (error) {
    // Surface a friendlier message if the verify endpoint is not found
    if (error?.response?.status === 404) {
      console.error('OTP verify endpoint not found at /verify-otp/.');
    }
    console.error('Verify OTP API Error:', error);
    throw error;
  }
};

export const getUserInfo = async () => {
  try {
    const response = await api.get(ENDPOINTS.USER_INFO);
    return response.data;
  } catch (error) {
    console.error('Error getting user info:', error);
    throw error;
  }
};

// Customer APIs
export const getCustomers = async (params = {}) => {
  try {
    const response = await api.get(ENDPOINTS.CUSTOMERS, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

// Create customer API
export const createCustomer = async (customerData) => {
  try {
    // Validate phone number if provided
    if (customerData.phone && !/^\d{10}$/.test(customerData.phone)) {
      throw new Error('Phone number must be exactly 10 digits');
    }

    const response = await api.post(ENDPOINTS.CUSTOMERS, customerData);
    return response.data;
  } catch (error) {
    console.error('Error creating customer:', error);
    throw error;
  }
};

// Market price API
export const getCurrentMarketPrice = async () => {
  try {
    const response = await api.get(ENDPOINTS.MARKET_PRICES);
    return response.data;
  } catch (error) {
    console.error('Error fetching market price:', error);
    throw error;
  }
};

// Update market price API
export const updateMarketPrice = async (price) => {
  try {
    // Validate that price is a positive number
    if (isNaN(price) || price < 0) {
      throw new Error('Price must be a positive number');
    }

    const response = await api.post(ENDPOINTS.MARKET_PRICES, { price });
    return response.data;
  } catch (error) {
    console.error('Error updating market price:', error);
    throw error;
  }
};

// Collection APIs
export const getCollections = async (params = {}) => {
  try {
    const response = await api.get(ENDPOINTS.COLLECTIONS, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching collections:', error);
    throw error;
  }
};

// Get collections filtered by date range
export const getCollectionsByDateRange = async (dateFrom, dateTo, ordering = null, params = {}) => {
  try {
    // Create params with EXPLICIT instructions to bypass all pagination
    const queryParams = {
      date_from: dateFrom,
      date_to: dateTo,
      fetch_all: true,
      page_size: 10000,
      limit: 10000,
      no_pagination: true
    };

    // Add ordering parameter if provided
    if (ordering) {
      queryParams.ordering = ordering;
    }

    // Add any additional params
    Object.assign(queryParams, params);

    // Log explicit attempt to bypass limits
    console.log('BYPASSING PAGINATION LIMITS: Fetching ALL collections by date range');
    console.log('Filter params:', queryParams);

    const response = await api.get(ENDPOINTS.COLLECTIONS, { params: queryParams });

    // Log details of what we received
    if (response.data) {
      const dataCount = response.data.results?.length || (Array.isArray(response.data) ? response.data.length : 0);
      console.log(`Received ${dataCount} records from API`);
      console.log(`Response type: ${Array.isArray(response.data) ? 'Array' : 'Object with results'}`);

      if (response.data.next) {
        console.warn('WARNING: There are more records available - increase page_size!');
      }
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching collections by date range:', error);
    throw error;
  }
};

// Get collections for a specific customer
export const getCustomerCollections = async (customerId, params = {}) => {
  try {
    // Create params object with very large page size
    const queryParams = {
      customer: customerId,
      fetch_all: true,
      page_size: 10000 // Use an extremely large page size to ensure we get ALL records
    };

    // Add any additional params like ordering
    Object.assign(queryParams, params);

    console.log('Fetching ALL collections for customer with params:', queryParams);
    const response = await api.get(ENDPOINTS.COLLECTIONS, { params: queryParams });
    return response.data;
  } catch (error) {
    console.error('Error fetching customer collections:', error);
    throw error;
  }
};

// Get collections filtered by both customer and date range
export const getFilteredCollections = async (options = {}) => {
  const { customerId, dateFrom, dateTo, ordering } = options;

  // Create params with EXPLICIT instructions to bypass all pagination
  const params = {
    fetch_all: true,
    page_size: 10000,
    limit: 10000,
    no_pagination: true
  };

  // Add customer filter if provided
  if (customerId) {
    params.customer = customerId;
  }

  // Add date filters if provided
  if (dateFrom) {
    params.date_from = dateFrom;
  }

  if (dateTo) {
    params.date_to = dateTo;
  }

  // Add ordering if provided
  if (ordering) {
    params.ordering = ordering;
  }

  // Log explicit attempt to bypass limits
  console.log('BYPASSING PAGINATION LIMITS: Fetching ALL filtered collections');
  console.log('Filter params:', params);

  try {
    const response = await api.get(ENDPOINTS.COLLECTIONS, { params });

    // Log details of what we received
    if (response.data) {
      const dataCount = response.data.results?.length || (Array.isArray(response.data) ? response.data.length : 0);
      console.log(`Received ${dataCount} records from API`);
      console.log(`Response type: ${Array.isArray(response.data) ? 'Array' : 'Object with results'}`);

      if (response.data.next) {
        console.warn('WARNING: There are more records available - increase page_size!');
      }
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching filtered collections:', error);
    throw error;
  }
};

export const createCollection = async (collectionData) => {
  try {
    const response = await api.post(ENDPOINTS.COLLECTIONS, collectionData);
    return response.data;
  } catch (error) {
    console.error('Error creating collection:', error);
    throw error;
  }
};

export const updateCollection = async (collectionId, collectionData) => {
  try {
    if (!collectionId) {
      throw new Error('Collection ID is required');
    }

    if (!collectionData.customer) {
      console.error('Error: Customer ID is missing in the collection data', collectionData);
      throw new Error('Customer ID is required');
    }

    // Log the structure of data being sent to the API
    console.log('Update collection API call with:');
    console.log('- Collection ID:', collectionId);
    console.log('- Customer ID:', collectionData.customer);
    console.log('- Full data:', JSON.stringify(collectionData, null, 2));

    // Make sure customer is sent as a number if it's stored as a string
    if (typeof collectionData.customer === 'string' && !isNaN(parseInt(collectionData.customer))) {
      collectionData.customer = parseInt(collectionData.customer);
    }

    const response = await api.put(`${ENDPOINTS.COLLECTIONS}${collectionId}/`, collectionData);
    console.log('Update collection success response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating collection:', error);

    // Enhance the error with more detail if available
    if (error.response && error.response.data) {
      console.error('Error details:', JSON.stringify(error.response.data, null, 2));
      const errorMessage = error.response.data.detail ||
        error.response.data.error ||
        'Failed to update collection';
      throw new Error(errorMessage);
    }

    throw new Error(error.message || 'Failed to update collection');
  }
};

// Delete a collection
export const deleteCollection = async (collectionId) => {
  try {
    if (!collectionId) {
      throw new Error('Collection ID is required');
    }

    console.log('Delete collection API call with ID:', collectionId);
    const response = await api.delete(`${ENDPOINTS.COLLECTIONS}${collectionId}/`);
    console.log('Delete collection success response:', response);
    return response.data;
  } catch (error) {
    console.error('Error deleting collection:', error);

    // Enhance the error with more detail if available
    if (error.response && error.response.data) {
      console.error('Error details:', JSON.stringify(error.response.data, null, 2));
      const errorMessage = error.response.data.detail ||
        error.response.data.error ||
        'Failed to delete collection';
      throw new Error(errorMessage);
    }

    throw new Error(error.message || 'Failed to delete collection');
  }
};

// Update added milk rate collection API
export const updateAddedMilkRateCollection = async (collectionId, collectionData) => {
  try {
    const response = await api.patch(`${ENDPOINTS.COLLECTIONS}${collectionId}/`, collectionData);
    return response.data;
  } catch (error) {
    console.error('Error updating added milk rate collection:', error);
    throw error;
  }
};

// Bulk collection API
export const createBulkCollections = async (collectionsArray) => {
  try {
    // Log the data being sent for debugging
    console.log('Sending collection data:', JSON.stringify(collectionsArray, null, 2));

    if (!Array.isArray(collectionsArray) || collectionsArray.length === 0) {
      throw new Error('No collections to submit');
    }

    // For single collection submission
    if (collectionsArray.length === 1) {
      const response = await api.post(ENDPOINTS.COLLECTIONS, collectionsArray[0]);
      return [response.data];
    }

    // For multiple collections, create an array of promises for each collection
    const createPromises = collectionsArray.map(collection =>
      api.post(ENDPOINTS.COLLECTIONS, collection)
    );

    // Execute all requests and wait for them to complete
    const responses = await Promise.all(createPromises);

    // Return array of response data
    return responses.map(response => response.data);
  } catch (error) {
    console.error('Error creating bulk collections:', error);
    // Provide more detailed error information
    if (error.response && error.response.data) {
      console.error('Error details:', JSON.stringify(error.response.data, null, 2));
      const errorMessage = error.response.data.detail || error.response.data.error || 'Database Error';
      const customError = new Error(errorMessage);
      customError.detail = error.response.data;
      throw customError;
    }
    throw error;
  }
};

// Add dairy information endpoints
export const getDairyInfo = async () => {
  try {
    const response = await api.get(ENDPOINTS.DAIRY_INFO);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      const errorMessage = 'No dairy information found. Please set up your dairy first.';
      console.error(errorMessage);
      throw new Error(errorMessage);
    }
    console.error('Error fetching dairy information:', error);
    throw error;
  }
};

// Add function to update dairy information
export const updateDairyInfo = async (dairyData) => {
  try {
    // Basic validation
    if (!dairyData.dairy_name || !dairyData.dairy_name.trim()) {
      throw new Error('Dairy name is required');
    }

    // Ensure rate_type is a valid value
    if (dairyData.rate_type && !['fat_snf', 'fat_clr'].includes(dairyData.rate_type)) {
      throw new Error('Invalid rate type. Must be either fat_snf or fat_clr');
    }

    // Make the API call
    const response = await api.post(ENDPOINTS.DAIRY_INFO, dairyData);
    return response.data;
  } catch (error) {
    console.error('Error updating dairy information:', error);
    throw error;
  }
};

// Add wallet balance endpoint
export const getWalletBalance = async () => {
  try {
    const response = await api.get(ENDPOINTS.WALLET);
    console.log('Raw wallet API response:', response);

    // Return the data from the response
    return response.data;
  } catch (error) {
    console.error('Error fetching wallet balance:', error);

    // Provide a more detailed error message with status code
    if (error.response) {
      console.error(`Wallet API error: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
    }

    // Return null instead of throwing to prevent breaking the app
    return null;
  }
};

// Raw Collection APIs
export const getRawCollections = async (params = {}) => {
  try {
    // If we have filters and no explicit pagination, fetch all records
    const queryParams = { ...params };

    // Set fetch_all parameter when filters are applied but no pagination is explicitly requested
    if ((params.customer || params.date_from || params.date_to) && !params.page && !params.page_size) {
      queryParams.fetch_all = true;
    }

    const response = await api.get(ENDPOINTS.RAW_COLLECTIONS, { params: queryParams });
    return response.data;
  } catch (error) {
    console.error('Error fetching raw collections:', error);
    throw error;
  }
};

export const getRawCollectionDetails = async (id) => {
  try {
    const response = await api.get(`${ENDPOINTS.RAW_COLLECTIONS}${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching raw collection details:', error);
    throw error;
  }
};

export const createRawCollection = async (collectionData) => {
  try {
    const response = await api.post(ENDPOINTS.RAW_COLLECTIONS, collectionData);
    return response.data;
  } catch (error) {
    console.error('Error creating raw collection:', error);
    throw error;
  }
};

export const updateRawCollection = async (id, collectionData) => {
  try {
    const response = await api.patch(`${ENDPOINTS.RAW_COLLECTIONS}${id}/`, collectionData);
    return response.data;
  } catch (error) {
    console.error('Error updating raw collection:', error);
    throw error;
  }
};

export const deleteRawCollection = async (id) => {
  try {
    if (!id) {
      throw new Error('Collection ID is required');
    }

    const response = await api.delete(`${ENDPOINTS.RAW_COLLECTIONS}${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting raw collection:', error);

    // Enhance the error with more detail if available
    if (error.response && error.response.data) {
      console.error('Error details:', JSON.stringify(error.response.data, null, 2));
      const errorMessage = error.response.data.detail ||
        error.response.data.error ||
        'Failed to delete raw collection';
      throw new Error(errorMessage);
    }

    throw new Error(error.message || 'Failed to delete raw collection');
  }
};

export const addMilkRateToRawCollection = async (id, rateData) => {
  try {
    const response = await api.patch(`${ENDPOINTS.RAW_COLLECTIONS}${id}/add-milk-rate/`, rateData);
    return response.data;
  } catch (error) {
    console.error('Error adding milk rate to raw collection:', error);
    throw error;
  }
};

export const createBulkRawCollections = async (collectionsArray) => {
  try {
    // Create a copy of the array to avoid modifying the original
    const collectionsToSubmit = [...collectionsArray];

    // Process each collection individually
    const responses = [];
    for (const collection of collectionsToSubmit) {
      try {
        const response = await createRawCollection(collection);
        responses.push({
          success: true,
          data: response,
          message: 'Raw collection created successfully'
        });
      } catch (error) {
        responses.push({
          success: false,
          error: error.error || 'Failed to create raw collection',
          collection
        });
      }
    }

    return {
      results: responses,
      successCount: responses.filter(r => r.success).length,
      failureCount: responses.filter(r => !r.success).length
    };
  } catch (error) {
    console.error('Error in bulk raw collections creation:', error);
    throw error;
  }
};

// Pro-Rata Report APIs
export const generateProRataPurchaseReport = async (startDate, endDate) => {
  try {
    // Format DD-MM-YYYY expected by the API
    const response = await api.get(ENDPOINTS.PRO_RATA_PURCHASE_REPORT, {
      params: {
        start_date: startDate,
        end_date: endDate
      },
      responseType: 'blob', // Important: to handle PDF response
    });

    return response;
  } catch (error) {
    console.error('Error generating pro-rata purchase report:', error);
    throw error;
  }
};

export const generateProRataPurchaseSummaryReport = async (startDate, endDate) => {
  try {
    const response = await api.get(ENDPOINTS.PRO_RATA_PURCHASE_SUMMARY_REPORT, {
      params: {
        start_date: startDate,
        end_date: endDate
      },
      responseType: 'blob', // Important: to handle PDF response
    });

    return response;
  } catch (error) {
    console.error('Error generating pro-rata purchase summary report:', error);
    throw error;
  }
};

export const generateProRataFullReport = async (startDate, endDate) => {
  try {
    const response = await api.get(ENDPOINTS.PRO_RATA_FULL_REPORT, {
      params: {
        start_date: startDate,
        end_date: endDate
      },
      responseType: 'blob', // Important: to handle PDF response
    });

    return response;
  } catch (error) {
    console.error('Error generating pro-rata full report:', error);
    throw error;
  }
};

export const generateProRataCustomerBills = async (startDate, endDate) => {
  try {
    const response = await api.get(ENDPOINTS.PRO_RATA_CUSTOMER_BILLS, {
      params: {
        start_date: startDate,
        end_date: endDate
      },
      responseType: 'blob', // Important: to handle PDF response
    });

    return response;
  } catch (error) {
    console.error('Error generating pro-rata customer bills:', error);
    throw error;
  }
};

export const generateProRataCustomerReport = async (startDate, endDate, customerIds) => {
  try {
    const response = await api.get(ENDPOINTS.PRO_RATA_CUSTOMER_REPORT, {
      params: {
        start_date: startDate,
        end_date: endDate,
        customer_ids: customerIds.join(',') // Convert array to comma-separated string
      },
      responseType: 'blob', // Important: to handle PDF response
    });

    return response;
  } catch (error) {
    console.error('Error generating pro-rata customer report:', error);
    throw error;
  }
};

export const getProRataPurchaseSummaryData = async (startDate, endDate) => {
  try {
    const response = await api.get(ENDPOINTS.PRO_RATA_PURCHASE_SUMMARY_DATA, {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching pro-rata purchase summary data:', error);
    throw error;
  }
}; 
